import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContact } from '@/context/ContactContext';
import EnquiryPopup from '@/components/EnquiryPopup';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const location = useLocation();
  const { contactSettings } = useContact();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Properties', path: '/properties' },
    { name: 'Office Space', path: '/properties/office' },
    { name: 'Retail', path: '/properties/retail' },
    { name: 'SCO Plots', path: '/properties/sco' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-md' : ''
      }`}
      style={{
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(12px)',
        boxShadow: isScrolled
          ? '0 1px 0 rgba(0,0,0,0.05), 0 2px 0 rgba(201,168,76,0.15)'
          : '0 1px 0 rgba(201,168,76,0.1)',
        height: 80,
      }}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src="/images/urbancial-logo.png" alt="URBANCIAL" style={{ height: 64 }} />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="relative text-[10px] uppercase tracking-[0.2em] font-medium transition-colors"
                style={{
                  fontFamily: "'Josefin Sans', sans-serif",
                  fontWeight: 400,
                  color: isActive(link.path) ? '#8a6a10' : '#5a4a20',
                }}
                onMouseEnter={(e) => { e.target.style.color = '#8a6a10'; }}
                onMouseLeave={(e) => { if (!isActive(link.path)) e.target.style.color = '#5a4a20'; }}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg,#c9a84c,#8a6a10)' }} />
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a href={`tel:${contactSettings.phone_primary?.replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-sm transition-colors"
              style={{ color: '#5a4a20', fontFamily: "'Josefin Sans', sans-serif" }}
              data-testid="phone-link">
              <Phone className="w-4 h-4" style={{ color: '#8a6a10' }} />
              <span className="font-medium text-xs">{contactSettings.phone_primary}</span>
            </a>
            <button
              className="px-6 py-2.5 rounded text-[10px] uppercase tracking-[0.15em] font-semibold text-white transition-all duration-300"
              style={{
                fontFamily: "'Josefin Sans', sans-serif",
                background: 'linear-gradient(135deg,#8a6a10,#6a4e08)',
              }}
              onClick={() => setShowEnquiry(true)}
              data-testid="enquire-now-btn"
              onMouseEnter={(e) => { e.target.style.background = 'linear-gradient(135deg,#6a4e08,#4a3208)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'linear-gradient(135deg,#8a6a10,#6a4e08)'; }}
            >
              Enquire Now
            </button>
          </div>

          <button className="lg:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-testid="mobile-menu-toggle">
            {isMobileMenuOpen ? <X className="w-6 h-6" style={{ color: '#2a1e00' }} /> : <Menu className="w-6 h-6" style={{ color: '#2a1e00' }} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden" style={{ background: '#fff', borderTop: '1px solid rgba(201,168,76,0.15)' }} data-testid="mobile-menu">
          <nav className="flex flex-col py-4">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className="px-6 py-3 text-sm uppercase tracking-wider font-medium"
                style={{
                  fontFamily: "'Josefin Sans', sans-serif",
                  color: isActive(link.path) ? '#8a6a10' : '#2a1e00',
                  background: isActive(link.path) ? 'rgba(201,168,76,0.08)' : 'transparent',
                }}
                onClick={() => setIsMobileMenuOpen(false)}>
                {link.name}
              </Link>
            ))}
            <div className="px-6 py-4" style={{ borderTop: '1px solid rgba(201,168,76,0.15)', marginTop: 8 }}>
              <button className="w-full py-3 rounded text-xs uppercase tracking-widest font-semibold text-white"
                style={{ fontFamily: "'Josefin Sans', sans-serif", background: 'linear-gradient(135deg,#8a6a10,#6a4e08)' }}
                onClick={() => { setIsMobileMenuOpen(false); setShowEnquiry(true); }}>
                Enquire Now
              </button>
            </div>
          </nav>
        </div>
      )}

      <EnquiryPopup open={showEnquiry} onClose={() => setShowEnquiry(false)} />
    </header>
  );
};

export default Header;
